import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../design/Icon';
import { useAppTo } from './links';
import { HOME, SERVICES, isActivePath, isAvailable } from './services';

const SOON = 'Bientôt disponible';
const byId = (id: string) => SERVICES.find((s) => s.id === id)!;

/** Encoche du `TabBar` du design system (`Design system/components/navigation/TabBar.jsx`) : arc de rayon 42 sous le bouton central. */
const NOTCH = 'M0 20A42 42 0 0 0 84 20';

/**
 * Barre d'onglets mobile : 5 icônes sans libellé visible (nom accessible par `aria-label`). Accueil est le cercle
 * central de 60 px qui déborde vers le haut, le bord de la barre se courbe autour de lui ; l'onglet actif porte un point
 * de 4 px, Accueil actif un anneau `ink`.
 */
export function TabBar({ pathname, appsOpen, onToggleApps, onHome }: { pathname: string; appsOpen: boolean; onToggleApps: () => void; onHome: () => void }) {
  const to = useAppTo();
  const homeActive = isActivePath(pathname, HOME.path);

  const serviceTab = (id: string) => {
    const service = byId(id);
    const icon = (
      <>
        <Icon name={service.icon} size={24} />
        <span className="sh-tab__dot" />
      </>
    );
    if (!isAvailable(service)) {
      return (
        <button type="button" className="sh-tab" aria-label={service.label} aria-disabled="true" title={`${service.label} · ${SOON}`}>
          {icon}
        </button>
      );
    }
    const active = isActivePath(pathname, service.path);
    return (
      <Link to={to(service.path!)} className="sh-tab" aria-label={service.label} aria-current={active ? 'page' : undefined} onClick={appsOpen ? onToggleApps : undefined}>
        {icon}
      </Link>
    );
  };

  const plain = (icon: IconName, label: string) => (
    <button type="button" className="sh-tab" aria-label={label} aria-disabled="true" title={`${label} · ${SOON}`}>
      <Icon name={icon} size={24} />
      <span className="sh-tab__dot" />
    </button>
  );

  return (
    <nav className="sh-tabbar" aria-label="Navigation principale">
      <div className="sh-tabbar__bg" aria-hidden="true">
        <div className="sh-tabbar__side" />
        <div className="sh-tabbar__notch">
          <svg width="84" height="84" viewBox="0 0 84 84" style={{ display: 'block' }}>
            <path d={`${NOTCH}V84H0Z`} style={{ fill: 'var(--surface)' }} />
            <path d={NOTCH} fill="none" style={{ stroke: 'var(--line)' }} strokeWidth="1" />
          </svg>
          <div className="sh-tabbar__fill" />
        </div>
        <div className="sh-tabbar__side" />
      </div>
      <div className="sh-tabbar__row">
        {serviceTab('passwords')}
        {serviceTab('notes')}
        <span />
        {plain('search', 'Chercher')}
        <button type="button" className="sh-tab" aria-label="Apps" aria-expanded={appsOpen} onClick={onToggleApps}>
          <Icon name="grid" size={24} />
          <span className="sh-tab__dot" />
        </button>
      </div>
      <Link to={to(HOME.path!)} className="sh-tabbar__home" aria-label="Accueil" aria-current={homeActive && !appsOpen ? 'page' : undefined} onClick={onHome}>
        <Icon name="home" size={28} />
      </Link>
    </nav>
  );
}
